import React from "react";
import {
  Paper,
  Grid,
  Typography,
  Button,
  Box,
  CardMedia,
  Avatar,
  Stack,
  Chip,
  Divider,
  Container,
  IconButton,
} from "@mui/material";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";

// Sample blog data - in a real app this would come from an API
const sampleBlog = {
  id: "1",
  title: "Insights & Ideas: Exploring the World Through Words",
  date: "2024-02-08T08:30:00",
  publishedDate: "May 28, 2025",
  location: "Shivaji Nagar, Bangalore",
  image:
    "https://miro.medium.com/v2/resize:fit:679/1*wNnm98pkENYLJV5OeG1WvQ@2x.jpeg",
  author: {
    name: "Neelesh",
    avatar:
      "https://neoinpublic.blob.core.windows.net/image/GOVT%20ILOGO.png17805333-4991-457f-9f0f-fe2b536b7a5a?sp=racwdl&st=2024-12-16T11:31:13Z&se=2028-03-31T19:31:13Z&spr=https&sv=2022-11-02&sr=c&sig=6hKP78H%2BqpTmp%2FoOfM1Qvp5XffulMaVowsrgQhBa1vY%3D",
  },
  description:
    "A thoughtful blog sharing stories, opinions, tips, and inspiration on everything from personal growth and tech trends to creative pursuits and everyday life. Join the journey of discovery, one post at a time.",
  content: `
    <p>In today's fast-paced digital world, the art of meaningful writing has become more important than ever. This blog explores how words can shape our understanding of the world around us, connect diverse perspectives, and foster innovation.</p>
    
    <h2>The Power of Storytelling</h2>
    <p>Humans are wired for stories. From ancient cave paintings to modern social media, storytelling remains our most powerful communication tool. Research shows that stories are 22 times more memorable than facts alone. In our content strategy, we focus on narrative-driven pieces that:</p>
    <ul>
      <li>Create emotional connections with readers</li>
      <li>Simplify complex concepts through relatable examples</li>
      <li>Present multiple perspectives on contemporary issues</li>
      <li>Showcase real-world applications of abstract ideas</li>
    </ul>
    
    <h2>Bridging the Digital Divide</h2>
    <p>As technology advances at breakneck speed, a new form of literacy is emerging. Our tech literacy series breaks down topics like:</p>
    <ul>
      <li>Blockchain fundamentals beyond cryptocurrency</li>
      <li>Ethical AI implementation frameworks</li>
      <li>Data privacy in the age of surveillance capitalism</li>
      <li>Open-source movements and their impact on innovation</li>
    </ul>
    
    <p>By making technical concepts accessible, we empower readers to participate meaningfully in digital transformation.</p>
    
    <h2>The Creative Process</h2>
    <p>Creativity isn't magic—it's a discipline. Our monthly deep dives into creative methodologies explore:</p>
    <ul>
      <li>Design thinking principles for non-designers</li>
      <li>Overcoming creative blocks through constraint-based exercises</li>
      <li>Collaborative creation techniques for distributed teams</li>
      <li>Maintaining creative momentum in long-term projects</li>
    </ul>
    
    <blockquote>
      "Words are our most inexhaustible source of magic, capable of both inflicting injury and remedying it." - J.K. Rowling
    </blockquote>
    
    <h2>Join Our Community</h2>
    <p>With over 230,000 active readers across 152 countries, our community continues to grow organically. We host monthly virtual writing workshops, quarterly idea jams, and an annual Words Matter conference in Bangalore.</p>
  `,
  tags: [
    "Writing",
    "Technology",
    "Creativity",
    "Digital Literacy",
    "Community",
  ],
  stats: {
    views: 233650,
    shares: 8450,
    comments: 1243,
    readingTime: "8 min read",
  },
  related: [
    {
      id: "2",
      title: "The Art of Technical Writing",
      image:
        "https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80",
    },
    {
      id: "3",
      title: "Creative Thinking in Digital Age",
      image:
        "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80",
    },
  ],
};

const SingleBlogsScreen = () => {
  const { id } = useParams();
  const [saved, setSaved] = React.useState(false);
  const navigate = useNavigate();
  const blogData = [
    {
      id: "1",
      title: "Insights & Ideas: Exploring the World Through Words",
      description:
        "A thoughtful blog sharing stories, opinions, tips, and inspiration on everything from personal growth and tech trends to creative pursuits and everyday life.",
      image:
        "https://miro.medium.com/v2/resize:fit:679/1*wNnm98pkENYLJV5OeG1WvQ@2x.jpeg",
      author: {
        name: "Neelesh",
        avatar:
          "https://neoinpublic.blob.core.windows.net/image/GOVT%20ILOGO.png17805333-4991-457f-9f0f-fe2b536b7a5a?sp=racwdl&st=2024-12-16T11:31:13Z&se=2028-03-31T19:31:13Z&spr=https&sv=2022-11-02&sr=c&sig=6hKP78H%2BqpTmp%2FoOfM1Qvp5XffulMaVowsrgQhBa1vY%3D",
      },
      postDate: "May 28, 2025",
    },
    {
      id: "2",
      title: "The Future of Web Development in 2025",
      description:
        "Exploring emerging trends in web development including AI integration, WebAssembly, and new CSS features that will shape the future of user experiences.",
      image:
        "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      author: {
        name: "Sarah Johnson",
        avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      },
      postDate: "June 12, 2025",
    },
    {
      id: "3",
      title: "Sustainable Architecture in Urban Environments",
      description:
        "How modern cities are incorporating green design principles to combat climate change while improving quality of life for residents.",
      image:
        "https://images.unsplash.com/photo-1486175060817-5663aacc6655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1476&q=80",
      author: {
        name: "Michael Chen",
        avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      },
      postDate: "May 15, 2025",
    },
    {
      id: "4",
      title: "Mindfulness Techniques for Busy Professionals",
      description:
        "Practical daily exercises to reduce stress and improve focus without adding extra time to your packed schedule.",
      image:
        "https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1479&q=80",
      author: {
        name: "Priya Sharma",
        avatar: "https://randomuser.me/api/portraits/women/68.jpg",
      },
      postDate: "June 5, 2025",
    },
    {
      id: "5",
      title: "Blockchain Beyond Cryptocurrency",
      description:
        "Exploring real-world applications of blockchain technology in supply chain management, healthcare records, and digital identity verification.",
      image:
        "https://images.unsplash.com/photo-1620336655052-b57986f5a26a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      author: {
        name: "David Kim",
        avatar: "https://randomuser.me/api/portraits/men/76.jpg",
      },
      postDate: "May 20, 2025",
    },
  ];

  const currentBlog = blogData.find((blog) => blog.id === id);

  if (!currentBlog) {
    return <Typography>Blog not found</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
        onClick={() => navigate(-1)}
      >
        Back to Blogs
      </Button>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden", mb: 4 }}>
        <CardMedia
          component="img"
          image={currentBlog?.image}
          alt={currentBlog?.title}
          sx={{
            height: { xs: 300, md: 450 },
            width: "100%",
            objectFit: "contain",
          }}
        />

        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {currentBlog?.title}
            </Typography>
            <IconButton onClick={() => setSaved(!saved)}>
              {saved ? (
                <BookmarkIcon color="primary" />
              ) : (
                <BookmarkBorderIcon />
              )}
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Avatar
              src={currentBlog?.author?.avatar}
              alt={currentBlog?.author?.name}
            />
            <Box>
              <Typography fontWeight="medium">
                {currentBlog?.author?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentBlog?.publishedDate} · {currentBlog?.stats?.readingTime}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mb: 3 }}>
            {currentBlog?.tags?.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                variant="outlined"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>

          <Box
            sx={{
              "& h2": { mt: 4, mb: 2, fontSize: "1.5rem", fontWeight: "bold" },
              "& p": { mb: 2, lineHeight: 1.8 },
              "& ul": { pl: 3, mb: 2 },
              "& li": { mb: 1 },
              "& blockquote": {
                borderLeft: "4px solid",
                borderColor: "divider",
                pl: 2,
                py: 1,
                mb: 2,
                fontStyle: "italic",
              },
            }}
            dangerouslySetInnerHTML={{ __html: sampleBlog?.content }}
          />

          <Divider sx={{ my: 4 }} />

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ShareRoundedIcon />}
              sx={{ textTransform: "none" }}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              startIcon={<LocationOnOutlinedIcon />}
              sx={{ textTransform: "none" }}
            >
              {currentBlog?.location}
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={4}
            justifyContent="flex-start"
            sx={{ color: "text.secondary" }}
          >
            <Typography>
              {currentBlog?.stats?.views.toLocaleString()} views
            </Typography>
            <Typography>{currentBlog?.stats?.comments} comments</Typography>
            <Typography>{currentBlog?.stats?.shares} shares</Typography>
          </Stack>
        </Box>
      </Paper>

      <Typography variant="h5" fontWeight="bold" mb={3}>
        Related Blogs
      </Typography>
      <Grid container spacing={3}>
        {sampleBlog?.related?.map((blog) => (
          <Grid item xs={12} sm={6} md={4} key={blog?.id}>
            <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <CardMedia
                component="img"
                height="180"
                image={blog?.image}
                alt={blog?.title}
              />
              <Box sx={{ p: 2 }}>
                <Typography fontWeight="bold">{blog?.title}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default SingleBlogsScreen;
